import React from 'react';
import StoreUtils from "../../services/Store/StoreUtils";
import HoursHelper from "../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../services/Common/Date/DateService";
import {ConfigApi, ConfigHoursApi, GroupedHours} from "../../services/Api/types";
import {useTranslation} from "react-i18next";
import {isTimeAllDay} from "../../services/Common/Date/DateHelper";

interface Props {
    config: ConfigApi
    openHours: ConfigHoursApi[] | undefined;
    locale: string | undefined;
    allGroupedHours: GroupedHours;
}

const TodayOpeningHours = ({config, openHours, locale, allGroupedHours}: Props) => {
    const {t} = useTranslation();

    const drawOpenedHours = (index: number, openingHours: ConfigHoursApi[]) => {
        if (isTimeAllDay(openingHours[0].hour_to) && isTimeAllDay(openingHours[0].hour_from)) return t("common.word.whole_day");

        if (index === 0) {
            let result = t("common.word.open_to", {time: openingHours[0].hour_to});

            if (openingHours.length > 1) {
                const restOfOpeningHours = openingHours.slice(1);
                const restHours: string[] = [];

                restOfOpeningHours.forEach(openingHour => {
                    restHours.push(openingHour.hour_from + "-" + openingHour.hour_to);
                });

                if (restHours.length > 0) {
                    result += ` ${t("common.word.and")} ${restHours.join(", ")}`
                }
            }

            return result;
        }
    }

    const drawContent = () => {
        if (StoreUtils.isPaused(config)) {
            return (
                <span className="opening-hours-warning">{t("common.word.temporarily_close")}</span>
            );
        }

        const mergedHours = HoursHelper.getMergedHourRanges(openHours ?? []);
        const allHours = Object.values(allGroupedHours).flat();
        const now = DateService.now();

        if (!openHours || openHours.length === 0) {
            return (
                <span className="d-inline-flex gap-1">
                    <span className="opening-hours-danger">
                        {`${t("enums.store_status.closed")}.`}
                    </span>
                     <span>
                        {HoursHelper.getNextOpening(allHours, now, t, true, locale)}
                    </span>
                </span>
            );
        }

        const isOpenNow = HoursHelper.isOpen(config.hours, now);

        if (!isOpenNow) {
            const nextOpeningDate = HoursHelper.getNextOpening(allHours, now, t, true, locale);
            const anyOpeningDate = nextOpeningDate !== t("enums.store_status.closed");

            return (
                <span className="d-inline-flex gap-1">
                    <span className="opening-hours-danger">
                        {anyOpeningDate ? `${t("enums.store_status.closed")}.` : nextOpeningDate}
                    </span>
                    {
                        anyOpeningDate && (
                            <span>
                                {nextOpeningDate}
                            </span>
                        )
                    }
                </span>
            )
        }

        const restOfOpeningHours = mergedHours.filter(mergedHour => {
            const mergedHourDate = DateService.now();
            const [hour, minute] = mergedHour.hour_to.split(":");

            if (isTimeAllDay(mergedHour.hour_from) && isTimeAllDay(mergedHour.hour_to)) {
                mergedHourDate.setDate(mergedHourDate.getDate() + 1);
            }
            mergedHourDate.setHours(Number(hour), Number(minute), 0);

            return mergedHourDate > now;
        })

        return restOfOpeningHours.map((h, index) => (
            <span
                className="opening-hours-success"
                key={index + "-" + h.hour_from + "-" + h.hour_to}
            >
                {drawOpenedHours(index, restOfOpeningHours)}
            </span>
        ));
    }

    return (
        <span className="opening-hours d-flex flex-column">
            {drawContent()}
        </span>
    )
}

export default TodayOpeningHours;